// ---------------------------------------------------------------------------
// V30 — THE COMPLIANCE CENTRE. `console.compliance`, at `compliance`.
//
// Three questions, in the order somebody actually asks them:
//
//   1. What here decides things automatically, and which of those decide about a
//      PERSON. Directive 2024/2831 §1.1's first obligation, and the answer has to
//      be readable by the worker rather than by a lawyer.
//   2. If one of them decided something about me, who do I ask. A named human,
//      derived from the permission the review route needs — so the list empties
//      the day nobody holds it, which is a finding the screen states rather than
//      a blank it hides.
//   3. What can never be stored, and where the database is standing over it.
//
// ---------------------------------------------------------------------------
// THE REGISTER IS RENDERED, NEVER INFERRED
//
// Nothing on this screen decides whether a system is explainable, whether it
// affects a worker or whether it has a review route. Every one of those is a
// column the migration asserted against the catalogue, and a browser that
// inferred any of them would be a second register — assembled where nobody
// checks it, from rules somebody would have to remember to keep in step.
//
// ---------------------------------------------------------------------------
// WHY THE PROHIBITED-DATA SECTION SAYS WHAT THE GUARD CANNOT DO
//
// A token match cannot read intent and a careful writer defeats it. A screen that
// presented it as a solved problem would be the most dangerous thing in the
// slice: somebody would relax the thing that actually works, which is that this
// product has no field inviting the data in the first place. So the section leads
// with the count of fields that ACCEPT PROSE AT ALL, and the guard is described
// as the backstop it is.
//
// NO NEW CSS. Every class here already carries a rule in `web/src/design`.
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";

import { may, send } from "../api/auth";
import {
  type WorkingTimeDraft,
  type WorkingTimeView,
  compliance as complianceApi,
  type ComplianceRegister,
  type ComplianceReviews,
} from "../api/compliance";
import { AddButton } from "../design/AddLink";
import { Notice } from "../design/Notice";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import { Tabs } from "../design/Tabs";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { whenIn } from "../system/format";
import { WorkingTimeExceptions } from "./WorkingTimeExceptions";

export function Compliance() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();

  // THE ORGANISATION'S CLOCK. `HANDOFF.md` §6's ruling and V27a's `whenIn`: a
  // queue read at 23:00 in one country about a decision taken at 08:00 in another
  // is the ordinary case for an agency working across a border, and a time
  // rendered in the reader's browser zone is a different time from the one in the
  // audit row it came from.
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;

  const [recording, setRecording] = useState(false);
  const [register, setRegister] = useState<ComplianceRegister | null>(null);
  // Each read keeps its status, so a limit and a fault read differently (design/LoadFailed).
  const [registerFailed, setRegisterFailed] = useState<Failure | null>(null);
  const [reviews, setReviews] = useState<ComplianceReviews | null>(null);
  const [reviewsFailed, setReviewsFailed] = useState<Failure | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [wt, setWt] = useState<WorkingTimeView | null>(null);
  const [wtFailed, setWtFailed] = useState<Failure | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveOutcome, setSaveOutcome] = useState<{ tone: "live" | "failed"; message: string } | null>(null);

  async function loadWorkingTime() {
    const res = await send<WorkingTimeView & { error?: { message: string } }>(
      complianceApi.workingTime(apiBase),
    );
    if (res.status === 200 && res.body?.baseline) {
      setWt(res.body);
      setWtFailed(null);
    } else {
      setWt(null);
      setWtFailed({ status: res.status, refusal: refusal(res.body?.error) });
    }
  }

  // ★ THE REFUSAL IS THE DATABASE'S AND IT IS SHOWN VERBATIM. It names the field
  // and the figure — "may not raise the weekly maximum above 48.00 hours" — and
  // re-wording it here would be a second copy of a rule this screen does not own.
  async function saveAgreement(event: React.FormEvent) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setSaveOutcome(null);
    const num = (k: string) => (draft[k] && draft[k].trim() !== "" ? Number(draft[k]) : undefined);
    const payload: WorkingTimeDraft = {
      agreement_reference: draft.agreement_reference ?? "",
      agreed_on: draft.agreed_on ?? "",
      max_weekly_hours: num("max_weekly_hours"),
      max_daily_hours: num("max_daily_hours"),
      reference_period_days: num("reference_period_days"),
      min_daily_rest_hours: num("min_daily_rest_hours"),
      min_weekly_rest_hours: num("min_weekly_rest_hours"),
      max_consecutive_days: num("max_consecutive_days"),
    };
    try {
      const res = await send<{ error?: { message: string } }>(
        complianceApi.recordWorkingTime(apiBase, payload),
      );
      if (res.status === 200) {
        setSaveOutcome({ tone: "live", message: t("The agreement is recorded and in force.") });
        setDraft({});
        await loadWorkingTime();
      } else {
        setSaveOutcome({
          tone: "failed",
          message: refusal(res.body?.error) ?? t("That agreement was not recorded."),
        });
      }
    } catch {
      setSaveOutcome({ tone: "failed", message: t("We could not reach Verbunk.") });
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    void loadWorkingTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  // Named, so "Try again" can read each one again.
  async function loadRegister() {
    const res = await send<ComplianceRegister & { error?: { message: string } }>(complianceApi.register(apiBase));
    if (res.status === 200 && Array.isArray(res.body?.systems)) {
      setRegister(res.body);
      setRegisterFailed(null);
    } else {
      setRegister(null);
      setRegisterFailed({ status: res.status, refusal: refusal(res.body?.error) });
    }
  }

  async function loadReviews() {
    const res = await send<ComplianceReviews & { error?: { message: string } }>(complianceApi.reviews(apiBase));
    if (res.status === 200 && Array.isArray(res.body?.queue)) {
      setReviews(res.body);
      setReviewsFailed(null);
    } else {
      setReviews(null);
      setReviewsFailed({ status: res.status, refusal: refusal(res.body?.error) });
    }
  }

  useEffect(() => {
    void loadRegister();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  useEffect(() => {
    void loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  return (
    // `stack`, WHICH IS WHAT EVERY OTHER SCREEN USES. This shell was written as
    // `screen` and `screen-head` — two class names with no rule anywhere in
    // web/src/design, so every element under them rendered with NO STYLING AT
    // ALL. Nine inherited children caught it the first time anything ran against
    // this slice, and the gallery would have shown it to the owner: a third
    // spelling of an existing class is not a new class.
    <div className="stack">
      <h1>{t("Compliance")}</h1>
      <p className="muted">
        {t("What decides things here without a person pressing anything, what it reads, how it explains itself, and who to ask when it gets something wrong.")}
      </p>

      {registerFailed !== null ? (
        <section className="panel">
          <LoadFailed failure={registerFailed} fallback={t("The register could not be read.")} onRetry={() => void loadRegister()} />
        </section>
      ) : null}


      <Tabs
        label={t("Compliance")}
        tabs={[
          { id: "systems", label: t("Automated systems"), content: (
<>
      {register !== null ? (
        <section className="panel">
          <h2>{t("Automated systems")}</h2>
          <p className="muted">
            {t("{n} system(s) in this product. {affecting} of them decide something about a person, and each of those either has a route to a human or says plainly why no human here can change the answer.", {
              n: register.systems.length,
              affecting: register.worker_affecting_count,
            })}
          </p>
          {register.systems.length === 0 ? (
            <p className="muted">
              {t("The register is empty, which cannot be right — this product ranks candidates and scores reliability. Report it rather than reading it as “nothing is automated”.")}
            </p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t("System")}</th>
                  <th scope="col">{t("What it does")}</th>
                  <th scope="col">{t("Asking a human")}</th>
                  <th scope="col">{""}</th>
                </tr>
              </thead>
              <tbody>
                {register.systems.map((s) => (
                  <>
                    <tr key={s.system_id}>
                      <td>
                        {translateLabel(locale, s.label)}
                        <div role="group" aria-label={t("About a person")}>
                        {/* NEVER COLOUR ALONE, and never a bare yes: "decides about
                            a person" and "suggests an order to a person" are the two
                            sides of the status presumption in §1.2, and one word for
                            both would be the most misleading cell on the screen. */}
                        <StatusChip
                          tone={s.affects_worker ? "attention" : "neutral"}
                          label={
                            s.affects_worker
                              ? s.is_automated_decision
                                ? t("Decides about a person")
                                : t("Affects a person, advisory only")
                              : t("Not about a person")
                          }
                        />
                      </div>
                      </td>
                      <td>{translateLabel(locale, s.purpose)}</td>
                      <td>
                        {s.review_route !== null ? (
                          <>
                            <StatusChip tone="live" label={t("A human can set it aside")} />
                            {(s.review_roles ?? []).length > 0 ? (
                              <p className="muted">
                                {t("Can be set aside by: {roles}", { roles: (s.review_roles ?? []).map((r) => translateLabel(locale, r)).join(", ") })}
                              </p>
                            ) : null}
                          </>
                        ) : (
                          <StatusChip
                            tone="neutral"
                            label={s.no_review_reason_label === null ? t("No route recorded") : translateLabel(locale, s.no_review_reason_label)}
                          />
                        )}
                      </td>
                      <td className="nowrap">
                        <button
                          type="button"
                          className="btn btn-quiet"
                          aria-expanded={open === s.system_id}
                          onClick={() =>
                            setOpen(open === s.system_id ? null : s.system_id)
                          }
                        >
                          {open === s.system_id ? t("Hide what it reads") : t("What it reads")}
                        </button>
                      </td>
                    </tr>
                    {open === s.system_id ? (
                      <tr key={`${s.system_id}-detail`}>
                        <td colSpan={4}>
                          <ul>
                            {s.inputs.map((line) => (
                              <li key={line}>{translateLabel(locale, line)}</li>
                            ))}
                          </ul>
                          <p className="muted">{translateLabel(locale, s.explanation_sentence)}</p>
                        </td>
                      </tr>
                    ) : null}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ) : null}

</>
) },
          { id: "review", label: t("Asking a human"), content: (
<>
      <section className="panel">
        <h2>{t("Asking a human")}</h2>
        {reviewsFailed !== null ? (
          <>
            <LoadFailed failure={reviewsFailed} fallback={t("The review queue could not be read.")} onRetry={() => void loadReviews()} />
            <p className="muted">
              {t("The register above is unaffected — it describes the software rather than this organisation's decisions.")}
            </p>
          </>
        ) : reviews === null ? (
          <p className="muted">{t("Reading the queue…")}</p>
        ) : (
          <>
            {reviews.reviewers.length === 0 ? (
              // AN EMPTY LIST IS A FINDING. A register that names a review route
              // in an organisation where nobody holds the permission has named a
              // door with nobody behind it, and that is exactly the rubber stamp
              // the directive is aimed at.
              //
              // UNREACHABLE ON THIS ENDPOINT TODAY, and kept deliberately. The
              // handler asks `app.compliance_reviewers` for `compliance.read`
              // alone, and the function refuses a caller who does not hold it —
              // so a successful call always names at least the reader. The state
              // is real for the other permissions the register names
              // (`match.override`, `credential.override`, `risk.override`), which
              // this endpoint does not yet ask about. V31 owns that; measured by
              // scripts/sql/v30-compliance-isolation.sql §5.5.
              <Notice tone="attention">{t("Nobody in this organisation holds the permission to decide a review")}</Notice>
            ) : (
              <p className="muted">
                {t("{n} person(s) here can decide a review: {who}.", {
                  n: reviews.reviewers.length,
                  who: reviews.reviewers.map((r) => r.reviewer_email).join(", "),
                })}
              </p>
            )}
            {reviews.queue.length === 0 ? (
              <p className="muted">
                {t("Nothing has been asked yet. That is the ordinary state — a queue with rows in it is not a sign of trouble either.")}
              </p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">{t("System")}</th>
                    <th scope="col">{t("About")}</th>
                    <th scope="col">{t("Asked")}</th>
                    <th scope="col">{t("Where it stands")}</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.queue.map((row) => (
                    <tr key={`${row.system_id}-${row.subject_id}-${row.opened_at}`}>
                      <td>{row.system_label}</td>
                      <td>{row.subject_kind}</td>
                      <td>{whenIn(row.opened_at, orgZone)}</td>
                      <td>
                        <StatusChip
                          tone={row.is_decided ? "live" : "attention"}
                          label={
                            row.is_decided
                              ? `${row.outcome_label === null ? t("Decided") : translateLabel(locale, row.outcome_label)} · ${whenIn(row.decided_at ?? row.opened_at, orgZone)}`
                              : t("Waiting for a person")
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </section>
</>
) },
          { id: "working-time", label: t("Working time"), content: (
<>
      {/* 0110 — §3's SECOND LEVEL. Both figures are shown because one alone
            cannot say whether a limit is the statute or this agency's own
            agreement, and that is the only question asked of this panel. */}
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Working time")}</h2>
          <AddButton label={t("Record an agreement")} expanded={recording} onClick={() => setRecording(!recording)} />
        </div>
        {wtFailed !== null ? (
          <LoadFailed failure={wtFailed} fallback={t("The working-time limits could not be read.")} onRetry={() => void loadWorkingTime()} />
        ) : wt === null ? (
          <p className="muted">{t("Reading the limits…")}</p>
        ) : (
          <>
            <p className="muted">
              {wt.agreement === null
                ? t("No collective agreement is recorded, so the statutory limits for this country are the limits in force.")
                : t("Narrowed by {reference}, agreed {agreed}.", {
                    reference: wt.agreement.agreement_reference,
                    agreed: wt.agreement.agreed_on,
                  })}
            </p>
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t("Limit")}</th>
                  <th className="numeric">{t("Statute")}</th>
                  <th className="numeric">{t("In force")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t("Weekly maximum (hours)")}</td>
                  <td className="numeric">{String(wt.baseline?.max_weekly_hours ?? "—")}</td>
                  <td className="numeric">{String(wt.resolved?.max_weekly_hours ?? "—")}</td>
                </tr>
                <tr>
                  <td>{t("Daily maximum (hours)")}</td>
                  <td className="numeric">{String(wt.baseline?.max_daily_hours ?? "—")}</td>
                  <td className="numeric">{String(wt.resolved?.max_daily_hours ?? "—")}</td>
                </tr>
                <tr>
                  <td>{t("Working-time frame (days)")}</td>
                  <td className="numeric">{String(wt.baseline?.reference_period_days ?? "—")}</td>
                  <td className="numeric">{String(wt.resolved?.reference_period_days ?? "—")}</td>
                </tr>
                <tr>
                  <td>{t("Daily rest (hours)")}</td>
                  <td className="numeric">{String(wt.baseline?.min_daily_rest_hours ?? "—")}</td>
                  <td className="numeric">{String(wt.resolved?.min_daily_rest_hours ?? "—")}</td>
                </tr>
                <tr>
                  <td>{t("Weekly rest (hours)")}</td>
                  <td className="numeric">{String(wt.baseline?.min_weekly_rest_hours ?? "—")}</td>
                  <td className="numeric">{String(wt.resolved?.min_weekly_rest_hours ?? "—")}</td>
                </tr>
                <tr>
                  <td>{t("Consecutive days")}</td>
                  <td className="numeric">{String(wt.baseline?.max_consecutive_days ?? "—")}</td>
                  <td className="numeric">{String(wt.resolved?.max_consecutive_days ?? "—")}</td>
                </tr>
              </tbody>
            </table>
            <p className="field-hint">{wt.resolved?.source ?? ""}</p>
          </>
        )}

        {/* An agreement may only NARROW a limit. The database refuses anything
            else and names the field; this form states the rule and does not
            keep a second copy of it. */}
        {recording ? (
        <form className="stack" onSubmit={saveAgreement}>
          <p className="field-hint">
            {t("A collective agreement may only make a limit stricter. Leave a box empty to leave that limit as the statute sets it.")}
          </p>
          <div className="form-grid">
          <div className="field">
            <label htmlFor="wt-ref">{t("Agreement")}</label>
            <input
              id="wt-ref"
              value={draft.agreement_reference ?? ""}
              onChange={(e) => setDraft({ ...draft, agreement_reference: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="wt-date">{t("Agreed on")}</label>
            <p className="field-hint">{t("A working-time frame runs from this day. Without one the limit is a plain 48 hours in each calendar week.")}</p>
            <input
              id="wt-date"
              type="date"
              value={draft.agreed_on ?? ""}
              onChange={(e) => setDraft({ ...draft, agreed_on: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="wt-max_weekly_hours">{t("Weekly maximum (hours)")}</label>
            <input
              id="wt-max_weekly_hours"
              inputMode="decimal"
              value={draft.max_weekly_hours ?? ""}
              onChange={(e) => setDraft({ ...draft, max_weekly_hours: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="wt-max_daily_hours">{t("Daily maximum (hours)")}</label>
            <input
              id="wt-max_daily_hours"
              inputMode="decimal"
              value={draft.max_daily_hours ?? ""}
              onChange={(e) => setDraft({ ...draft, max_daily_hours: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="wt-reference_period_days">{t("Working-time frame (days)")}</label>
            <input
              id="wt-reference_period_days"
              inputMode="decimal"
              value={draft.reference_period_days ?? ""}
              onChange={(e) => setDraft({ ...draft, reference_period_days: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="wt-min_daily_rest_hours">{t("Daily rest (hours)")}</label>
            <input
              id="wt-min_daily_rest_hours"
              inputMode="decimal"
              value={draft.min_daily_rest_hours ?? ""}
              onChange={(e) => setDraft({ ...draft, min_daily_rest_hours: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="wt-min_weekly_rest_hours">{t("Weekly rest (hours)")}</label>
            <input
              id="wt-min_weekly_rest_hours"
              inputMode="decimal"
              value={draft.min_weekly_rest_hours ?? ""}
              onChange={(e) => setDraft({ ...draft, min_weekly_rest_hours: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="wt-max_consecutive_days">{t("Consecutive days")}</label>
            <input
              id="wt-max_consecutive_days"
              inputMode="decimal"
              value={draft.max_consecutive_days ?? ""}
              onChange={(e) => setDraft({ ...draft, max_consecutive_days: e.target.value })}
            />
          </div>
          </div>
          {saveOutcome !== null ? (
            <div className="form-message" role="status" aria-live="polite">
              <StatusChip
                tone={saveOutcome.tone === "live" ? "live" : "failed"}
                label={saveOutcome.tone === "live" ? t("Recorded") : t("Not recorded")}
              />
              <p>{saveOutcome.message}</p>
            </div>
          ) : null}
          <div className="row">
            <button className="btn" type="submit" disabled={saving}>
              {saving ? t("Working…") : t("Record agreement")}
            </button>
            <button className="btn btn-quiet" type="button" onClick={() => setRecording(false)}>
              {t("Cancel")}
            </button>
          </div>
        </form>
        ) : null}
      </section>

</>
) },
          { id: "geofence-dpia", label: t("Geofence DPIA"), content: <GeofenceDpia mayRecord={me ? may(me, "config.manage") : false} /> },
          { id: "exceptions", label: t("Working-time exceptions"), content: (
<>
<WorkingTimeExceptions />
</>
) },
          { id: "never-stored", label: t("What can never be stored"), content: (
<>
      {register !== null ? (
        <section className="panel">
          <h2>{t("What can never be stored")}</h2>
          <p className="muted">
            {t("{n} class(es) of data this product refuses to hold about anybody. The refusal is in the database, so it holds whatever screen or script the write came from.", {
              n: register.prohibited_classes.length,
            })}
          </p>
          <ul>
            {register.prohibited_classes.map((c) => (
              <li key={c.class_id}>
                <strong>{translateLabel(locale, c.class_label)}</strong> — {c.description}
              </li>
            ))}
          </ul>
          <p className="muted">
            {t("Of {fields} text field(s) a person can write, {scanned} accept whole sentences and are the only ones where this data could arrive by accident. The database refuses a write naming one of the classes above — but that is a backstop and not the control that matters. The control that matters is that no field in this product asks anybody for this data: an appeal against a reliability score, for instance, carries a reason from a fixed list and has no free text anywhere.", {
              fields: register.free_text_fields.length,
              scanned: register.scanned_field_count,
            })}
          </p>
          {/* Tour T-114. The 28 that take sentences are the ones that matter; the rest cannot hold one. */}
          {(() => {
            const scanned = register.free_text_fields.filter((f) => f.is_scanned);
            const others = register.free_text_fields.filter((f) => !f.is_scanned);
            return (
              <>
                <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t("Field")}</th>
                <th scope="col">{t("What goes in it")}</th>
                <th scope="col">{t("Who writes it")}</th>
              </tr>
            </thead>
            <tbody>
              {scanned.map((f) => (
                <tr key={`${f.relation}.${f.attribute}`}>
                  <td>
                    {f.relation}.{f.attribute}
                  </td>
                  <td>{translateLabel(locale, f.content_label)}</td>
                  <td>{translateLabel(locale, f.author_label)}</td>
                </tr>
              ))}
            </tbody>
          </table>
                {others.length > 0 ? (
                  <details>
                    <summary>{t("{n} field(s) that cannot hold a sentence", { n: others.length })}</summary>
                    <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t("Field")}</th>
                <th scope="col">{t("What goes in it")}</th>
                <th scope="col">{t("Who writes it")}</th>
              </tr>
            </thead>
            <tbody>
              {others.map((f) => (
                <tr key={`${f.relation}.${f.attribute}`}>
                  <td>
                    {f.relation}.{f.attribute}
                  </td>
                  <td>{translateLabel(locale, f.content_label)}</td>
                  <td>{translateLabel(locale, f.author_label)}</td>
                </tr>
              ))}
            </tbody>
          </table>
                  </details>
                ) : null}
              </>
            );
          })()}
        </section>
      ) : null}
</>
) },
        ]}
      />
    </div>
  );
}

/**
 * Answer 54 (0332). The agency records its DPIA as controller; until then a check-in goes through
 * and no position is used.
 */
function GeofenceDpia({ mayRecord }: { mayRecord: boolean }) {
  const { apiBase } = useSession();
  const { t } = useT();
  const [dpia, setDpia] = useState<{ reference: string; completed_on: string } | null | undefined>(undefined);
  const [reference, setReference] = useState("");
  const [completedOn, setCompletedOn] = useState("");
  const [failed, setFailed] = useState<string | null>(null);

  const load = () =>
    void send<{ dpia?: { reference: string; completed_on: string } | null }>(complianceApi.geofenceDpia(apiBase)).then((res) =>
      res.status === 200 ? setDpia(res.body.dpia ?? null) : setFailed(t("The DPIA record could not be read.")),
    );
  useEffect(load, [apiBase]);

  async function record(event: React.FormEvent) {
    event.preventDefault();
    const res = await send<{ error?: { message?: string } }>(
      complianceApi.recordGeofenceDpia(apiBase, { reference, completed_on: completedOn }),
    );
    if (res.status === 200) { setFailed(null); load(); } else setFailed(res.body.error?.message ?? t("The DPIA could not be recorded."));
  }

  return (
    <section className="panel">
      <h2>{t("Geofence DPIA")}</h2>
      <p className="muted">
        {dpia
          ? t("Recorded: {reference}, completed {date}. Check-ins by geofence use the position to judge distance, and the position is not stored.", { reference: dpia.reference, date: dpia.completed_on })
          : t("Not recorded. A check-in by geofence still goes through, and no position is asked for or used, until your organisation records its DPIA.")}
      </p>
      {failed !== null ? <StatusChip tone="failed" label={failed} /> : null}
      {mayRecord ? (
        <form onSubmit={(e) => void record(e)}>
          <div className="field">
            <label htmlFor="dpia-ref">{t("Where the DPIA is kept")}</label>
            <input id="dpia-ref" required minLength={3} maxLength={200} value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="dpia-date">{t("Completed on")}</label>
            <input id="dpia-date" type="date" required value={completedOn} onChange={(e) => setCompletedOn(e.target.value)} />
          </div>
          <button className="btn" type="submit">{t("Record the DPIA")}</button>
        </form>
      ) : null}
    </section>
  );
}
